import { DefaultProductSorting, useSorting } from '@/composables/useSorting'
import { useTableSelection } from '@/composables/useTableSelection'
import { useTranslation } from '@/composables/useTranslation'
import { ContextMenu, ContextMenuOption } from '@/models/ContextMenu'
import { BarcodeOption, BarcodeType } from '@/models/Ean'
import { Cell, Coord, SelectionBoundaries } from '@/models/GenericInterfaces'
import { BasicOption, CopiedData, Option } from '@/models/Global'
import { ProductInput, termsSplitRegex } from '@/models/Import'
import Message from '@/models/Message'
import { ProductPropertyStatus, ProductPropertyStatuses, SimplifiedProduct } from '@/models/Product'
import { AdvancedProperty, EditableAdvancedProperty, EmptyCheckedPlaceholder } from '@/models/Property'
import DataService from '@/services/DataService'
import { browserStorage } from '@/utils/browserStorage'
import { getCellId } from '@/utils/bulkUtils'
import {
  getBarcodeType,
  getProductPropertyId,
  getTaxonomyFromProperty,
  productPropertyIsDeletable,
  productPropertyIsEditable,
  productPropertyStatusIsEditable,
  propertyCanGenerateEan,
  propertyHasStaticValues,
  valueTypeIsNumeric,
} from '@/utils/propertyUtils'
import {
  areComparable,
  clone,
  copiedCellDataShouldBeQuoted,
  getIndentation,
  hasValue,
  metaKey,
  parseTabularData,
  textIsSelected,
  toComparable,
  toNumber,
  toSafeString,
} from '@/utils/tools'
import { defineStore } from 'pinia'
import { capitalize, computed, ComputedRef, reactive, Ref, ref, toRef, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDefaultStore } from '.'
import { useProductsStore } from './productsArchiveStore'
import { useProductsBulkStore } from './productsBulkStore'
import { usePropertyStore } from './propertyStore'
import { useTaxonomiesStore } from './taxonomiesStore'

export const useProductsTableUtilityStore = defineStore('products-table-utility-store', () => {
  interface CellData {
    product: SimplifiedProduct
    property?: EditableAdvancedProperty
    productInput?: ProductInput
  }

  // --------------------------
  // -- Stores & composables --
  // --------------------------
  const bulkStore = useProductsBulkStore()
  const productsStore = useProductsStore()
  const store = useDefaultStore()
  const propertyStore = usePropertyStore()

  const { appTranslate, translate } = useTranslation()
  const { sortBy } = useSorting()
  const route = useRoute()
  const router = useRouter()
  const tableSelection = useTableSelection()

  // -------------------
  // -- Reactive vars --
  // -------------------
  const popOvers = ref<Array<Coord & { msg: string; list?: string[] }>>([])
  const selectedIsActive = ref(false)
  const contextMenu = ref<ContextMenu>()
  const copyDragSources = reactive<Set<string>>(new Set())

  const cellRefsData: { [id: string]: HTMLElement | null } = {}
  const propertyRefsData: {
    [id: string]: { focusField: (focusedByKeyboard?: boolean, shouldClear?: boolean) => void } // TODO bad typing
  } = {}

  const focusedRows = ref<Set<number>>()

  const isSideScrolled = ref(false)

  const copied = ref<{ data?: CopiedData[][]; copyString?: string; boundaries: SelectionBoundaries; selectedIds: Set<string> }>()

  const productsPendingDeletion = ref<string[]>()

  const clearCellsOnPaste = ref<Cell[]>()

  // --------------------
  // -- Computed props --
  // --------------------
  const selectedPropsAndProducts = computed(() =>
    selected.value.reduce((acc: Array<{ product: SimplifiedProduct; property: AdvancedProperty }>, { product, property }) => {
      if (product && property) {
        acc.push({ product, property })
      }
      return acc
    }, []),
  )

  const selected = computed(() => getCellData(tableSelection.selectedCells.value))

  const activeCellIsEditable = computed(() => {
    if (tableSelection.activeCell.value) {
      const product = bulkStore.sessionProducts[tableSelection.activeCell.value.rowIndex]

      if (product) {
        const productInput = bulkStore.currentState[product.sku]
        const property = bulkStore.combinedProperties[tableSelection.activeCell.value.columnIndex]
        return tableSelection.activeCell.value.rowIndex < bulkStore.sessionProducts.length && productPropertyIsEditable(property, product, productInput)
      }
    }
    return false
  })

  const selectedCellsAreEditable = computed(() =>
    selected.value.every(({ property, product, productInput }) => property && productPropertyIsEditable(property, product, productInput)),
  )

  const selectedCellsAreDeletable = computed(() =>
    selected.value.every(({ property, product, productInput }) => property && productPropertyIsDeletable(property, product, productInput)),
  )

  const activeCellRect = computed(() => (tableSelection.activeCell.value ? cellRefs.get(tableSelection.activeCell.value)?.getBoundingClientRect() : undefined))

  // --------------
  // -- Watchers --
  // --------------
  watch(
    () => route.fullPath,
    () => {
      focusedRows.value = undefined
    },
  )

  // -------------
  // -- Methods --
  // -------------
  const cellRefs = {
    get: (cell: Cell) => cellRefsData[getCellId(cell)],
    set: (cell: Cell, el: unknown) => {
      cellRefsData[getCellId(cell)] = el as HTMLElement
    },
  }

  const propertyComponentRefs = {
    get: (cell: Cell) => propertyRefsData[getCellId(cell)],
    set: (cell: Cell, propertyComponent: any) => {
      propertyRefsData[getCellId(cell)] = propertyComponent
    },
  }

  // #region Context menus
  const openHeaderContextMenu = (event: MouseEvent, property: AdvancedProperty) => {
    if (contextMenu.value?.id === property.defaultHeader) {
      contextMenu.value = undefined
    } else {
      event.preventDefault()

      contextMenu.value = {
        id: property.defaultHeader,
        header: property.defaultHeader,
        position: {
          x: event.x,
          y: event.y,
        },
        options: [
          {
            type: 'action',
            label: `${appTranslate('Sort ascending')} (${property.valueType === 'string' ? appTranslate('A-Z') : '0-9'})`,
            callback: () => sortBy(property, 'asc'),
            disabled:
              (!route.query.sortFieldPath &&
                DefaultProductSorting.sortFieldPath === property.sortFieldPath &&
                (route.query.sortOrder === 'asc' || !route.query.sortOrder)) ||
              (route.query.sortFieldPath === property.sortFieldPath && route.query.sortOrder === 'asc'),
            icon: 'fa-long-arrow-up',
          },
          {
            type: 'action',
            label: `${appTranslate('Sort descending')} (${property.valueType === 'string' ? appTranslate('Z-A') : '9-0'})`,
            callback: () => sortBy(property, 'desc'),
            disabled: route.query.sortFieldPath === property.sortFieldPath && route.query.sortOrder === 'desc',
            icon: 'fa-long-arrow-down',
          },
          {
            type: 'action',
            label: appTranslate('Hide column'),
            callback: () => {
              const key = toComparable(property.defaultName)
              bulkStore.selectedPropertyKeys[key] = false
            },
            disabled: property.sticky,
            icon: 'fa-eye-slash',
          },
        ],
      }
    }
  }

  const getProductNavigationOption = (): ContextMenuOption => {
    const sku = bulkStore.allProducts[tableSelection.activeCell.value!.rowIndex]?.sku
    return {
      type: 'action',
      label: appTranslate('Show product'),
      callback: () => {
        if (sku) {
          if (bulkStore.editMode) {
            window.open(`/products/${sku}`, 'product')
          } else {
            router.push({
              name: 'product',
              state: { modal: true },
              params: { sku },
            })
          }
        }
      },
      disabled: selected.value.length > 1 || !sku || bulkStore.newProductsSkus?.has(sku),
      icon: bulkStore.editMode ? 'fa-external-link' : '',
    }
  }

  const openIndexContextMenu = (event: MouseEvent, isProtected?: boolean) => {
    const id = selected.value.map(({ product }) => product.sku).join('_')

    if (contextMenu.value?.id === id) {
      contextMenu.value = undefined
    } else {
      event.preventDefault()

      const products = selected.value.map(({ product }) => product)
      const selectedSkus = new Set(products.map((product) => product.relationKey || product.sku))

      contextMenu.value = {
        id,
        position: {
          x: event.x,
          y: event.y,
        },
        options: [
          {
            type: 'action',
            label: appTranslate('Reset/remove row(s)'),
            callback: () => {
              if (confirm(appTranslate('Do you want to reset or remove the selected product row(s)?'))) {
                products?.forEach((product) => {
                  const isNewProduct = !!bulkStore.newProductsSkus?.has(product.sku)

                  if (isNewProduct) {
                    bulkStore.removeNewProduct(product.sku)
                  } else if (bulkStore.isModified(product)) {
                    bulkStore.resetProduct(product)
                  }
                  tableSelection.clearSelected()
                })
              }
            },
            disabled: !bulkStore.editMode || isProtected,
            icon: 'fa-undo',
          },
          {
            type: 'separator',
          },
          getProductNavigationOption(),
          {
            type: 'action',
            label: appTranslate(`Show related products`),
            callback: () => {
              const query = {
                _relation: Array.from(selectedSkus).join(','),
                includeAllCultures: '1',
              }

              if (bulkStore.editMode) {
                window.open(`/products?${new URLSearchParams(query).toString()}`, '_blank')
              } else {
                router.push({ query })
              }
            },
            // disabled: props.editMode, // !selectedSkus.size,
            icon: bulkStore.editMode ? 'fa-external-link' : 'fa-filter',
          },
          {
            type: 'separator',
          },
          {
            type: 'action',
            label: appTranslate('Set/toggle row focus'),
            icon: 'fa-paint-brush',
            callback: () => {
              if (focusedRows.value) {
                selected.value.forEach(({ cell }) => {
                  if (focusedRows.value?.has(cell.rowIndex)) {
                    focusedRows.value?.delete(cell.rowIndex)
                  } else {
                    focusedRows.value?.add(cell.rowIndex)
                  }
                })
              } else {
                focusedRows.value = new Set(selected.value.map(({ cell }) => cell.rowIndex))
              }
            },
            info: appTranslate('Marks selected rows with a colour'),
          },
          {
            type: 'action',
            icon: 'fa-paint-brush icon-disabled',
            label: appTranslate('Clear focused rows'),
            callback: () => {
              focusedRows.value = undefined
            },
            disabled: !focusedRows.value?.size,
          },
        ],
      }

      if (store.userPrivileges.has('bulkDeleteProducts')) {
        const deleteProductsOption = {
          type: 'action' as const,
          icon: 'fa-trash',
          label: appTranslate('Delete products'),
          opensPrompt: true,
          disabled: bulkStore.editMode,
          callback: () => {
            productsPendingDeletion.value = selected.value.map(({ product }) => product.sku)
          },
        }

        contextMenu.value.options.push({ type: 'separator' })
        contextMenu.value.options.push(deleteProductsOption)
      }

      if (store.currentUser?.role === 'superAdmin') {
        const syncProductsOption = {
          type: 'action' as const,
          icon: 'fa-refresh',
          label: appTranslate('Sync to Jeeves'),
          opensPrompt: true,
          disabled: bulkStore.editMode,
          callback: () => {
            const skus = selected.value.map(({ product }) => product.sku)
            syncProductsToJeeves(skus)
          },
        }

        contextMenu.value.options.push({ type: 'separator' })
        contextMenu.value.options.push(syncProductsOption)
      }
    }
  }

  const openContextMenuSelected = async (event: MouseEvent, position: Coord, isProtected?: boolean) => {
    const id = selected.value.map(({ cell }) => getCellId(cell)).join('_')

    if (contextMenu.value?.id === id) {
      contextMenu.value = undefined
    } else {
      event.preventDefault()

      const aSelectedCellCanGenerateBarcode = selected.value.some(({ property }) => property && propertyCanGenerateEan(property))
      const aSelectedCellIsWritable = selected.value.some(
        ({ property, product, productInput }) => property && productPropertyIsEditable(property, product, productInput),
      )
      const aSelectedCellIsDeletable = selected.value.some(
        ({ property, product, productInput }) => property && productPropertyIsDeletable(property, product, productInput),
      )

      const aSelectedCellCanSetPreliminaryStatus = selected.value.some(
        ({ property, product, productInput }) => property && productPropertyStatusIsEditable('preliminary', property, product, productInput),
      )

      const aSelectedCellCanSetCheckedStatus = selected.value.some(
        ({ property, product, productInput }) => property && productPropertyStatusIsEditable('checked', property, product, productInput),
      )

      const aSelectedCellCanRemoveStatus = selected.value.some(
        ({ property, product, productInput }) => property && productPropertyStatusIsEditable(null, property, product, productInput),
      )

      const isWritable = bulkStore.editMode && aSelectedCellIsWritable && !isProtected
      const aCellIsDeletable = isWritable && aSelectedCellIsDeletable
      const allCellsAreDeletable = isWritable && selectedCellsAreDeletable.value
      const prelimStatusCanBeSet = bulkStore.editMode && aSelectedCellCanSetPreliminaryStatus && !isProtected
      const checkedStatusCanBeSet = bulkStore.editMode && aSelectedCellCanSetCheckedStatus && !isProtected
      const statusCanBeRemoved = bulkStore.editMode && aSelectedCellCanRemoveStatus && !isProtected

      const translatedMetaKey = appTranslate(metaKey)
      const shiftKey = appTranslate('Shift')
      const deleteKey = appTranslate('Del')
      const backspaceKey = appTranslate('Backspace')

      contextMenu.value = {
        id,
        position,
        options: [
          {
            type: 'action',
            label: appTranslate('Copy'),
            callback: () => copySelectedCellsContent(),
            icon: 'fa-copy',
            keyboardShortcut: `${translatedMetaKey}+C`,
          },
          {
            type: 'action',
            label: appTranslate('Cut'),
            callback: () => copySelectedCellsContent(true),
            icon: 'fa-cut',
            disabled: !allCellsAreDeletable,
            keyboardShortcut: `${translatedMetaKey}+X`,
          },
          {
            type: 'action',
            label: appTranslate('Paste'),
            callback: () => pasteToCells(),
            disabled: !isWritable,
            icon: 'fa-paste',
            keyboardShortcut: `${translatedMetaKey}+V`,
          },
          {
            type: 'action',
            label: appTranslate('Paste special'),
            callback: () => pasteToCells(undefined, true),
            disabled: !isWritable,
            icon: 'fa-paste',
            keyboardShortcut: `${translatedMetaKey}+${shiftKey}+V`,
            info: appTranslate('Transposes paste direction'),
          },
          {
            type: 'separator',
          },
          {
            type: 'action',
            label: appTranslate('Reset'),
            callback: () => resetSelectedCells(),
            disabled: !isWritable || !aSelectedCellCanRemoveStatus,
            icon: 'fa-undo',
            keyboardShortcut: `${shiftKey}+${deleteKey}`,
          },
          {
            type: 'action',
            label: appTranslate('Delete'),
            callback: () => deleteSelectedCells(),
            disabled: !aCellIsDeletable,
            icon: 'fa-trash-o',
            keyboardShortcut: `${deleteKey}, ${backspaceKey}`,
          },
          {
            type: 'separator',
          },
          {
            type: 'action',
            label: `<span class="preliminary-text">${appTranslate('Set preliminary status')}</span>`,
            callback: () => changeStatusSelected('preliminary'),
            disabled: !prelimStatusCanBeSet,
          },
          {
            type: 'action',
            label: `${appTranslate('Set to %s', [`<span class="not-applicable-text">${EmptyCheckedPlaceholder}</span>`])}`,
            info: appTranslate('Clears value(s) and sets status checked'),
            callback: () => setNASelected(),
            disabled: !checkedStatusCanBeSet,
          },
          {
            type: 'action',
            label: appTranslate(`Remove status`),
            callback: () => changeStatusSelected(null),
            disabled: !statusCanBeRemoved,
            icon: 'fa-minus',
            collapsedItems: ProductPropertyStatuses.map((status) => {
              const css = status === 'preliminary' ? 'preliminary-text' : status === 'checked' ? 'not-applicable-text' : ''
              const translationString = status === 'checked' ? 'Remove %s' : 'Remove %s status'
              const statusText = status === 'checked' ? EmptyCheckedPlaceholder : appTranslate(status, undefined, 'lowercase')

              return {
                label: appTranslate(translationString, [`<span class="${css}" style="text-decoration: line-through">${statusText}</span>`]),
                callback: () => changeStatusSelected(status, true),
              }
            }),
          },
          {
            type: 'separator',
          },
          {
            type: 'action',
            label: `${appTranslate('Generate EAN13')}<i class="fa fa-flask ml-1" title="${appTranslate('Experimental feature')}"></i>`,
            callback: () => generateBarcodesForSelectedTableCells(),
            hidden: !aSelectedCellCanGenerateBarcode,
            disabled: !bulkStore.editMode,
            icon: 'fa-barcode',
            collapsedItems: store.availableBarcodeOptions?.map((option) => ({
              label: `Use "${translate(option.title, 'ean')}"`,
              callback: () => generateBarcodesForSelectedTableCells(option),
              // icon: 'fa-exclamation-triangle',
              info: option.description,
              disabled: option.disabled,
            })),
          },
          {
            type: 'separator',
            hidden: !aSelectedCellCanGenerateBarcode,
            disabled: !bulkStore.editMode,
          },
          getProductNavigationOption(),
          {
            type: 'separator',
          },
          {
            type: 'action',
            label: appTranslate('Clear selection'),
            callback: () => {
              tableSelection.clearSelected()
            },
            disabled: !tableSelection.selectedCells.value.length,
          },
        ],
      }
    }
  }

  const handleOpenContextMenu = (event: MouseEvent, cell: Cell, isForExpired?: boolean) => {
    contextMenu.value = undefined
    tableSelection.cellIsSelected(cell) ? 0 : tableSelection.setCellSelection(cell)
    openContextMenuSelected(event, { x: event.x, y: event.y }, isForExpired)
  }

  // #endregion

  // #region Multicell functions
  const copySelectedCellsContent = (clearOnPaste?: boolean) => {
    if (!store.userPrivileges.has('export')) {
      return
    }

    if (textIsSelected()) {
      return
    }

    if (clearOnPaste && !selectedCellsAreDeletable.value) {
      activeCellRect.value && showNotWritablePopOver(activeCellRect.value, undefined, 'deletable')
      return
    }

    if (selected.value.length) {
      const data: CopiedData[][] = []

      selected.value.forEach(({ property, product, cell }) => {
        if (property && product && tableSelection.orderedSelectionData.value) {
          const row = cell.rowIndex - tableSelection.orderedSelectionData.value.topmostRow
          const column = cell.columnIndex - tableSelection.orderedSelectionData.value.leftmostColumn

          if (!data[row]) {
            data[row] = []
          }

          const productInput = bulkStore.currentState?.[product.sku]
          const current = property.get(product, productInput)
          data[row][column] = { text: toSafeString(current?.value), propertyReturn: current }
        }
      })

      copied.value = {
        data,
        copyString: getClipboardString(data),
        selectedIds: new Set(selected.value.map(({ product, property }) => (property && getProductPropertyId(product, property)) || 'unknown')),
        boundaries: clone(tableSelection.orderedSelectionData.value),
      }

      if (clearOnPaste) {
        clearCellsOnPaste.value = tableSelection.selectedCells.value
      } else {
        clearCellsOnPaste.value = undefined
      }

      browserStorage.set('productTable-copiedData', copied.value.data, 'localStorage')
      return navigator.clipboard?.writeText(copied.value.copyString || '')
    }
  }

  const getClipboardString = (copiedData?: CopiedData[][]) =>
    copiedData
      ?.map((row) =>
        row
          .map((cell) => {
            const text = toSafeString(cell.text)
            // Apply quotes if the cell contains a newline or tab to avoid misinterpretation
            if (copiedCellDataShouldBeQuoted(text)) {
              return `"${text.replace(/"/g, '""')}"` // Adds surrounding quotes for multiline cells and escapes double quotes
            }
            return text
          })
          .join('\t'),
      )
      .join('\n')

  const getCopied = async (e?: ClipboardEvent) => {
    const copiedData: CopiedData[][] = copied.value?.data || browserStorage.get('productTable-copiedData', 'localStorage') || []
    const copiedDataString = copied.value?.copyString || getClipboardString(copiedData)

    let clipboardData

    try {
      const clipboardItem = e?.clipboardData?.getData('text/plain') || (await navigator.clipboard?.readText())
      clipboardData = clipboardItem?.replace(/\r\n?/g, '\n')
    } catch (err) {
      if (!browserStorage.get('seen-clipboard-read-error', 'sessionStorage')) {
        store.addMessage(
          new Message(
            appTranslate(
              'PIM could not read the clipboard. To paste externally copied data, allow the application to read the clipboard from browser settings',
            ),
            undefined,
            'warning',
          ),
        )
      }
      browserStorage.set('seen-clipboard-read-error', 1, 'sessionStorage')
      console.log(err)
    }

    const clipboardStringEqualsCopiedData =
      clipboardData === undefined
        ? !!copied.value?.data // clipboard read failed — trust internal state
        : copiedDataString === clipboardData

    return {
      data: clipboardStringEqualsCopiedData ? copiedData : clipboardData ? parseTabularData(clipboardData) : copiedData,
      clipboardStringEqualsCopiedData,
    }
  }

  /**
   * Resets the copied data (used to mark copied cells)
   * @param clearClipboardIfCopiedFromApp whether to clear the os clipboard as well if the data was copied from the app
   */
  const clearCopied = async (clearClipboardIfCopiedFromApp = true) => {
    if (clearClipboardIfCopiedFromApp && copied.value?.copyString) {
      await navigator.clipboard?.writeText('')
    }

    browserStorage.delete('productTable-copiedData')
    copied.value = undefined
    clearCellsOnPaste.value = undefined
  }

  const clearCopiedIfAffected = (product: SimplifiedProduct, property: AdvancedProperty) => {
    if (copied.value?.selectedIds.has(getProductPropertyId(product, property))) {
      clearCopied(false)
    }
  }

  const pasteToCells = async (e?: ClipboardEvent, pasteHorizontally?: boolean) => {
    if (!tableSelection.orderedSelectionData.value) {
      return
    }

    const fails: string[] = []
    const skipNewValues = new Map<AdvancedProperty, Set<string>>() // values for which the confirmation dialogue has already been shown

    const { data, clipboardStringEqualsCopiedData } = await getCopied(e)

    if (!data?.length) {
      return
    }

    if (!clipboardStringEqualsCopiedData) {
      copied.value = undefined
      browserStorage.delete('productTable-copiedData')
    }

    if (!selectedCellsAreEditable.value) {
      activeCellRect.value && showNotWritablePopOver(activeCellRect.value)
      return
    }

    const targetCells = getPasteTargetCells(data, pasteHorizontally ? 'horizontal' : 'vertical')
    if (clearCellsOnPaste.value) {
      const indexedTargetCells = new Set(targetCells.inBounds.map((cell) => getCellId(cell)))
      clearCellsOnPaste.value = clearCellsOnPaste.value?.filter((cell) => !indexedTargetCells.has(getCellId(cell)))
    }

    const getValue = (value: string | number, property: AdvancedProperty) => {
      const numericValue = toNumber(value)
      if (!valueTypeIsNumeric(property.valueType) || !Number.isNaN(numericValue)) {
        if (property.valueType === 'int') {
          return Math.round(numericValue).toString()
        } else if (property.valueType === 'float') {
          return numericValue.toString()
          // } else if (property.valueType === 'date') {
          //   return
        }

        return value.toString()
      }

      fails.push(
        appTranslate(`Can not paste %s in %s. Value is not valid for the field`, [
          `<em>${value}</em>`,
          `<em>${property.header || property.defaultHeader}</em>`,
        ]),
      )
    }

    const findOption = (option: Option<string | number | undefined>, value: string | number) =>
      areComparable(option.value, value) || areComparable(option.displayValue, value)

    const cachedPropertyOptions: Record<string, BasicOption<string | number | undefined>[]> = {}

    for (const cell of targetCells.inBounds) {
      const product = bulkStore.allProducts[cell.rowIndex]
      const property = bulkStore.combinedProperties?.[cell.columnIndex]
      const productInput = product ? bulkStore.currentState[product?.sku] : undefined

      let propertyOptions = cachedPropertyOptions[property.defaultHeader]
      if (!propertyOptions) {
        propertyOptions = cachedPropertyOptions[property.defaultHeader] = await propertyStore.getPropertyOptionsAsync(property)
      }

      if (!property || !product || !productInput || !cell.data || !productPropertyIsEditable(property, product, productInput)) {
        continue
      }

      const current = property.get(product, productInput)
      let newOptions: Array<string | number> | null | undefined
      let newStatus: ProductPropertyStatus | null | undefined
      const header = property.header || property.defaultHeader

      if (clipboardStringEqualsCopiedData) {
        newStatus = cell.data?.propertyReturn?.status || null
      }

      if (!cell.data?.text?.trim()) {
        if (current?.value) {
          // delete
          newOptions = null
        }
      } else {
        // const copiedValues = property.allowMultipleValues ? cell.data.text?.split(termsSplitRegex)?.filter(Boolean) : [cell.data.text]
        const copiedValues = property.allowMultipleValues
          ? (cell.data.propertyReturn?.value?.toString() || cell.data.text)?.split(termsSplitRegex)?.filter(Boolean)
          : propertyHasStaticValues(property)
            ? [cell.data.text]
            : [cell.data.propertyReturn?.value || cell.data.text]

        const validValues = copiedValues.reduce(
          (acc, str) => {
            const comparableString = toComparable(str)

            if (hasValue(str) && !acc.set.has(comparableString)) {
              const value = getValue(str, property)

              if (value !== undefined) {
                acc.arr.push(value)
                acc.set.add(comparableString)
              }
            }
            return acc
          },
          { arr: [] as string[], set: new Set<string>() },
        ).arr

        // if (!validValues.length || copiedValues.length !== validValues.length) {
        if (!validValues.length) {
          continue
        }

        if (clipboardStringEqualsCopiedData) {
          newStatus = cell.data?.propertyReturn?.status || null
        }

        if (propertyHasStaticValues(property)) {
          newOptions = validValues
        } else {
          const missingValues = new Set<string>()

          // match or aggregate new values that needs to be created
          newOptions = []
          for (let i = 0; i < validValues.length; i++) {
            const value = validValues[i]
            const foundOption = propertyOptions?.find((option) => findOption(option, value))

            if (foundOption) {
              newOptions[i] = foundOption.value ?? foundOption.value!
            } else if (!skipNewValues.get(property)?.has(value)) {
              missingValues.add(value)
            }
          }

          if (missingValues.size) {
            const missingValuesString = Array.from(missingValues).join(', ')

            if (property.computedOptions || !store.settings?.app.allowNewValuesDuringPaste) {
              fails.push(
                appTranslate(`Can not paste %s since %s are not pre-existing value(s) in %s`, [
                  `<em>${cell.data.text}</em>`,
                  `<em>${missingValuesString}</em>`,
                  `<em>${header}</em>`,
                ]),
              )
              continue
            }

            if (confirm(appTranslate("'%s' does not exist in %s. Would you like to create them?", [missingValuesString, header]))) {
              // Create new values

              for (let i = 0; i < validValues.length; i++) {
                const value = validValues[i]
                if (!missingValues.has(value)) {
                  continue
                }

                propertyStore.addStagedOption(property, {
                  value,
                  ...(property.type === 'categories' && { parentId: validValues[i - 1]?.toString().toLowerCase() }),
                })
                newOptions[i] = value
                delete cachedPropertyOptions[property.defaultHeader]
              }
            } else {
              const skipped = skipNewValues.get(property) || new Set()
              for (const value of missingValues) {
                skipped.add(value)
              }
              skipNewValues.set(property, skipped)
            }
          }

          // if (newOptions.reduce((sum: number, o) => sum + (o === undefined ? 0 : 1), 0) !== validValues.length) {
          // unable to match or create all values. continue on to the next cell
          // continue
          // }
        }
      }

      newOptions = newOptions === null ? null : newOptions?.filter((o) => o || o === 0) // remove empty values. preserve null

      if (newOptions !== undefined || newStatus !== undefined) {
        // Set value(s) and/or status

        const modified = { property: false, status: false }

        if (newOptions !== undefined) {
          modified.property = property.set(productInput, newOptions, product)
        }

        if (newStatus !== undefined) {
          modified.status = property.setStatus(productInput, newStatus, product)
        }

        bulkStore.setModified(product.sku, property, modified.property || modified.status)
        propertyStore.incPropertyResetKey(product.sku, property.key)
      }

      if (clearCellsOnPaste.value) {
        deleteCells(getCellData(clearCellsOnPaste.value))
        clearCopied()
      }
    }

    if (targetCells.outOfBounds.length) {
      fails.push(`Can not paste data to ${targetCells.outOfBounds.length} cell(s) because they were out of bounds`)
    }

    // set new selection
    const startingCell = targetCells.inBounds[0]
    const endingCell = targetCells.inBounds[targetCells.inBounds.length - 1]

    if (startingCell && endingCell) {
      tableSelection.setCellSelection(targetCells.inBounds[0])
      endingCell && tableSelection.extendSelectionTo(endingCell)
    }

    if (fails.length) {
      const msg = appTranslate('Failed to copy data to some fields')
      const cellRect = cellRefs.get(endingCell)?.getBoundingClientRect()

      if (cellRect) {
        showPopOver({ x: cellRect.x + 8, y: cellRect.y + 8 }, msg)
      }

      store.addMessage(new Message(msg, fails.join('\n'), 'warning', 15))
    }

    bulkStore.pushHistoryEntry()
  }

  /**
   * Returns an array of cells targetted for pasting. Aims to mimic excel behaviour
   * Expands the range to the copied data range if selection is smaller than the copied data range
   * Expands the range to include repeat cells if the selected rows/columns is divisible by the copied data rows/columns
   * Used by pasteToCells
   */
  const legacy_getPasteTargetCells = (data: CopiedData[][]) => {
    type TargetCell = Cell & { data: CopiedData }

    const inBounds: TargetCell[] = []
    const outOfBounds: TargetCell[] = []

    if (tableSelection.orderedSelectionData.value) {
      const numCopiedRows = data.length
      const numCopiedCols = data[0]?.length || 0

      const selectedRows = tableSelection.orderedSelectionData.value.bottommostRow - tableSelection.orderedSelectionData.value.topmostRow + 1
      const selectedCols = tableSelection.orderedSelectionData.value.rightmostColumn - tableSelection.orderedSelectionData.value.leftmostColumn + 1

      const effectiveRows = Math.max(selectedRows, numCopiedRows)
      const effectiveCols = Math.max(selectedCols, numCopiedCols)

      const repeatRows = effectiveRows % numCopiedRows === 0
      const repeatCols = effectiveCols % numCopiedCols === 0

      for (let rowOffset = 0; rowOffset < effectiveRows; rowOffset++) {
        for (let colOffset = 0; colOffset < effectiveCols; colOffset++) {
          const rowIndex = tableSelection.orderedSelectionData.value.topmostRow + rowOffset
          const columnIndex = tableSelection.orderedSelectionData.value.leftmostColumn + colOffset

          const footerIsSelected = tableSelection.activeCell.value && tableSelection.activeCell.value?.rowIndex >= bulkStore.sessionProducts.length
          const maxRowIndex = footerIsSelected ? bulkStore.allProducts.length - 1 : bulkStore.sessionProducts.length - 1
          const maxColIndex = bulkStore.combinedProperties.length - 1

          if ((repeatRows || rowOffset < numCopiedRows) && (repeatCols || colOffset < numCopiedCols)) {
            const dataRow = rowOffset % data.length
            const dataColumn = colOffset % data[dataRow]?.length
            const cellData = data[dataRow]?.[dataColumn]

            if (rowIndex <= maxRowIndex && columnIndex <= maxColIndex) {
              inBounds.push({ rowIndex, columnIndex, data: cellData })
            } else {
              outOfBounds.push({ rowIndex, columnIndex, data: cellData })
            }
          }
        }
      }
    }

    return { inBounds, outOfBounds }
  }

  /**
   * Returns an array of cells targetted for pasting. Aims to mimic excel behaviour
   * Expands the range to the copied data range if selection is smaller than the copied data range
   * Expands the range to include repeat cells if the selected rows/columns is divisible by the copied data rows/columns
   * Used by pasteToCells
   */
  const getPasteTargetCells = (data: CopiedData[][], direction: 'vertical' | 'horizontal' = 'vertical') => {
    type TargetCell = Cell & { data: CopiedData }

    const inBounds: TargetCell[] = []
    const outOfBounds: TargetCell[] = []

    const selection = tableSelection.orderedSelectionData.value
    if (!selection) {
      return { inBounds, outOfBounds }
    }

    const sourceRowCount = data.length
    const sourceColCount = data.reduce((max, row) => Math.max(max, row?.length || 0), 0)

    if (!sourceRowCount || !sourceColCount) {
      return { inBounds, outOfBounds }
    }

    const selectedRows = selection.bottommostRow - selection.topmostRow + 1
    const selectedCols = selection.rightmostColumn - selection.leftmostColumn + 1

    // In horizontal mode, treat copied data as transposed for target-shaping/repetition.
    const copiedRows = direction === 'horizontal' ? sourceColCount : sourceRowCount
    const copiedCols = direction === 'horizontal' ? sourceRowCount : sourceColCount

    const effectiveRows = Math.max(selectedRows, copiedRows)
    const effectiveCols = Math.max(selectedCols, copiedCols)

    const repeatRows = effectiveRows % copiedRows === 0
    const repeatCols = effectiveCols % copiedCols === 0

    const footerIsSelected = !!tableSelection.activeCell.value && tableSelection.activeCell.value.rowIndex >= bulkStore.sessionProducts.length
    const maxRowIndex = footerIsSelected ? bulkStore.allProducts.length - 1 : bulkStore.sessionProducts.length - 1
    const maxColIndex = bulkStore.combinedProperties.length - 1

    for (let rowOffset = 0; rowOffset < effectiveRows; rowOffset++) {
      for (let colOffset = 0; colOffset < effectiveCols; colOffset++) {
        if ((!repeatRows && rowOffset >= copiedRows) || (!repeatCols && colOffset >= copiedCols)) {
          continue
        }

        const rowIndex = selection.topmostRow + rowOffset
        const columnIndex = selection.leftmostColumn + colOffset

        // Map target offsets to source offsets.
        const sourceRow = direction === 'horizontal' ? colOffset % sourceRowCount : rowOffset % sourceRowCount
        const sourceCol = direction === 'horizontal' ? rowOffset % sourceColCount : colOffset % sourceColCount
        const cellData = data[sourceRow]?.[sourceCol]

        // Ragged clipboard rows can produce empty source cells; skip them.
        if (!cellData) {
          continue
        }

        if (rowIndex <= maxRowIndex && columnIndex <= maxColIndex) {
          inBounds.push({ rowIndex, columnIndex, data: cellData })
        } else {
          outOfBounds.push({ rowIndex, columnIndex, data: cellData })
        }
      }
    }

    return { inBounds, outOfBounds }
  }

  const cellIsWithinCopyBoundary = (cell: Cell) => {
    return (
      copied.value &&
      copied.value.boundaries.topmostRow <= cell.rowIndex &&
      copied.value.boundaries.bottommostRow >= cell.rowIndex &&
      copied.value.boundaries.leftmostColumn <= cell.columnIndex &&
      copied.value.boundaries.rightmostColumn >= cell.columnIndex
    )
  }

  const resetSelectedCells = () => {
    let somethingWasReset = false

    selected.value.forEach(async ({ property, product, productInput }) => {
      if (property) {
        const valueWasReset = property.reset(productInput, product)
        const statusWasReset = property.resetStatus(productInput)

        if (valueWasReset || statusWasReset) {
          somethingWasReset = true
          propertyStore.incPropertyResetKey(product.sku, property.key)
          bulkStore.setModified(product.sku, property, false)
        }
      }
    })

    if (somethingWasReset) {
      bulkStore.pushHistoryEntry(selectedPropsAndProducts.value)
    }
  }

  const deleteSelectedCells = (pushHistoryEntry = true) => {
    deleteCells(selected.value)
    pushHistoryEntry && bulkStore.pushHistoryEntry(selectedPropsAndProducts.value)
  }

  const deleteCells = (cells: CellData[]) =>
    cells.forEach(({ property, product, productInput }) => {
      if (property && productInput && productPropertyIsDeletable(property, product, productInput)) {
        property.set(productInput, null, product)

        const current = property.get(product, productInput)
        let modified = current.modified?.status || current.modified?.value

        if (current.status !== 'preliminary') {
          const statusWasSet = property.setStatus(productInput, null, product)
          modified = modified || statusWasSet
        }

        propertyStore.incPropertyResetKey(product.sku, property.key)
        bulkStore.setModified(product.sku, property, modified)
      }
    })

  /**
   * Sets or remove specific status
   * @param status
   * @param removeStatus whether to remove the specified status or set it
   */
  const changeStatusSelected = (status: ProductPropertyStatus | null, removeStatus?: boolean) => {
    changeStatus(selected.value, status, removeStatus)
    bulkStore.pushHistoryEntry(selectedPropsAndProducts.value)
  }

  const changeStatus = (cells: CellData[], status: ProductPropertyStatus | null, removeStatus?: boolean) => {
    cells.forEach(({ property, product, productInput }) => {
      if (property && productInput) {
        const current = property.get(product, productInput)

        if (!removeStatus || current?.status === status) {
          const statusChanged = property.setStatus(productInput, removeStatus ? null : status, product)

          propertyStore.incPropertyResetKey(product.sku, property.key)
          bulkStore.setModified(product.sku, property, statusChanged || current?.modified?.value)
        }
      }
    })
  }

  const setNASelected = () => {
    const canSetStatus = selected.value.filter(
      ({ property, product, productInput }) => property && productPropertyStatusIsEditable('checked', property, product, productInput),
    )

    deleteCells(canSetStatus)
    changeStatus(canSetStatus, 'checked')
    bulkStore.pushHistoryEntry(selectedPropsAndProducts.value)
  }

  const generateBarcodesForSelectedTableCells = async (explicitAlgorithm?: BarcodeOption) => {
    const { productPreviews } = selected.value.reduce(
      (acc, { productInput, id }) => {
        if (!acc.added.has(id)) {
          acc.productPreviews.push({ ...productInput, sku: id })
          acc.added.add(id)
        }
        return acc
      },
      { productPreviews: [] as ProductInput[], added: new Set<string>() },
    )

    const barcodeTypes = selected.value.reduce((acc, { property }) => {
      if (property && propertyCanGenerateEan(property)) {
        const barcodeType = getBarcodeType(property)
        barcodeType && acc.add(barcodeType)
      }
      return acc
    }, new Set<BarcodeType>())

    const response = await DataService.generateEanCodes(productPreviews, Array.from(barcodeTypes), explicitAlgorithm?.id)

    selected.value.forEach(({ property, product, productInput, id }) => {
      if (property && propertyCanGenerateEan(property)) {
        const original = property.get(product, undefined)
        const current = property.get(product, productInput)

        const barcodeType = getBarcodeType(property)

        if (barcodeType) {
          const newBarcode = response.codes?.[id]?.[barcodeType]

          if (newBarcode) {
            if (original?.value == newBarcode) {
              property?.reset(productInput, product)
              propertyStore.incPropertyResetKey(product.sku, property.key)
              bulkStore.setModified(product.sku, property, false)
            } else if (current?.value != newBarcode) {
              property?.set(productInput, newBarcode, product)
              propertyStore.incPropertyResetKey(product.sku, property.key)
              bulkStore.setModified(product.sku, property, true)
            }
          }
        }
      }
    })

    if (response.failures.categorization.length) {
      store.addMessage(
        new Message(
          appTranslate('Failed to automatically categorize %s products for EAN-code generation', [response.failures.categorization?.length]),
          response.failures.categorization?.join(', '),
          'warning',
        ),
      )
    } else if (response.failures.generation.length) {
      const grouped = response.failures.generation.reduce((acc: Record<string, string[]>, { codeType, sku }) => {
        const arr = (acc[codeType] ||= [])
        arr.push(sku)
        return acc
      }, {})

      const message = Object.entries(grouped)
        .map(([codeType, skus]) => `<strong>${translate(`barcode ${codeType}`, 'attributes', 'capitalize')}</strong>: ${skus.join(', ')}`)
        .join('\n')

      store.addMessage(
        new Message(appTranslate('Failed to generate some codes for %s', [explicitAlgorithm ? ` "${explicitAlgorithm.title}"` : '']), message, 'warning'),
      )
    }

    bulkStore.pushHistoryEntry(selectedPropsAndProducts.value)
  }

  const startCopyDrag = (selectionLimit?: string) => {
    const sourceCells = tableSelection.selectedCells.value

    sourceCells.forEach((cell) => {
      copyDragSources.add(getCellId(cell))
    })

    const copyCallback = () => {
      if (tableSelection.orderedSelectionData.value) {
        for (const sourceCell of sourceCells) {
          const sourceProperty = bulkStore.combinedProperties?.[sourceCell.columnIndex]
          const sourceProduct = bulkStore.sessionProducts[sourceCell.rowIndex]

          if (!sourceProperty || !sourceProduct) {
            continue
          }

          const productInputSource = bulkStore.currentState[sourceProduct?.sku]

          const current = sourceProperty.get(sourceProduct, productInputSource)

          for (let i = tableSelection.orderedSelectionData.value.topmostRow; i <= tableSelection.orderedSelectionData.value.bottommostRow; i++) {
            const product = bulkStore.sessionProducts[i]
            if (i === sourceCell.rowIndex || !product) {
              continue
            }

            const productInput = bulkStore.currentState[product.sku]
            if (!productInput) {
              console.warn('Found no product input', product.sku)
              continue
            }

            const modified = {
              value: sourceProperty.set(productInput, current?.defaultOptions || null, product),
              status: sourceProperty.setStatus(productInput, current?.status || null, product),
            }

            bulkStore.setModified(product.sku, sourceProperty, modified.value || modified.status)
            propertyStore.incPropertyResetKey(product.sku, sourceProperty.key)
          }

          bulkStore.pushHistoryEntry(selectedPropsAndProducts.value)
        }
      }
    }

    copyDragSources.clear()
    tableSelection.startSelectionDrag(undefined, 'column', copyCallback, selectionLimit)
  }

  // #endregion

  // #region Popover functions
  // const showNotInEditModePopOverAtActiveCell = () => {
  //   const coord = getActiveCellCoord()
  //   if (coord) {
  //     showNotInEditModePopOver(coord)
  //   }
  // }

  const showNotInEditModePopOver = async (coord: Coord, positionAdjustment = 8, showPropertyOptions?: boolean) => {
    let list: string[] | undefined

    if (store.settings?.app.showPropertyOptionsInPopover && showPropertyOptions && tableSelection.activeCell.value) {
      const property = bulkStore.combinedProperties?.[tableSelection.activeCell.value.columnIndex]

      const options = await propertyStore.getPropertyOptionsAsync(property)
      list = options.map((o) => getIndentation(o.level) + o.displayValue)
    }
    showPopOver({ x: coord.x + positionAdjustment, y: coord.y + positionAdjustment }, appTranslate("You're not in edit mode"), list)
  }

  const showNotWritablePopOver = (coord: Coord, positionAdjustment = 8, action: 'editable' | 'deletable' = 'editable') => {
    showPopOver({ x: coord.x + positionAdjustment, y: coord.y + positionAdjustment }, appTranslate(`A cell in the selection is not ${action}`))
  }

  const showPopOver = (coord: Coord, msg: string, list?: string[]) => {
    popOvers.value.push({
      x: coord.x,
      y: coord.y,
      msg,
      list,
    })

    document.addEventListener('wheel', () => clearPopOvers(), { once: true })
  }

  const clearPopOvers = () => {
    popOvers.value.length = 0
  }
  // #endregion

  const deletePendingProducts = async () => {
    const skus = productsPendingDeletion.value
    if (skus) {
      await DataService.deleteProducts(skus)
      productsPendingDeletion.value = undefined
      productsStore.fetchProducts(true)
      tableSelection.clearSelected()

      store.addMessage(new Message(appTranslate('Delete %s product(s)', [skus.length]), skus.join(', '), 'success', 10))
    }
  }

  const syncProductsToJeeves = async (skus: string[]) => {
    if (confirm(`Send product data to Jeeves for ${skus.length} product(s)?`)) {
      const result = await DataService.triggerJeevesUpdate(skus)

      if (result?.msg) {
        store.addMessage(new Message(result.msg, 'info'))
      } else {
        result?.targets?.forEach((target) => {
          store.addMessage(new Message(target.err || `Sent product data to Jeeves ${target.name}`, skus.join('\n'), target.success ? 'success' : 'danger'))
        })
      }
    }
  }

  const getCellData = (cells: Cell[]) =>
    cells.reduce(
      (
        acc: Array<{
          id: string
          product: SimplifiedProduct
          productInput: ProductInput
          property: EditableAdvancedProperty | undefined
          cell: Cell
          readonly: boolean
        }>,
        cell,
      ) => {
        const product = bulkStore.allProducts?.[cell.rowIndex]

        if (product) {
          const productInput = bulkStore.currentState[product.sku]

          acc.push({
            id: productInput?.sku || product.sku,
            product,
            productInput,
            property: bulkStore.combinedProperties?.[cell.columnIndex],
            cell,
            readonly: cell.rowIndex > bulkStore.sessionProducts.length - 1, // true if is expired product
          })
        }
        return acc
      },
      [],
    )

  return {
    // vars
    isSideScrolled,
    contextMenu,
    copyDragSources,
    selectedIsActive,
    popOvers,

    activeCellRect,
    cellRefs,
    propertyComponentRefs,
    focusedRows,

    activeCellIsEditable,
    selectedCellsAreEditable,
    selectedCellsAreDeletable,
    copied,
    productsPendingDeletion,
    clearCellsOnPaste: computed(() => !!clearCellsOnPaste.value),

    clearCopied,
    clearCopiedIfAffected,

    // methods
    openContextMenuSelected,
    openHeaderContextMenu,
    openIndexContextMenu,
    startCopyDrag,
    resetSelectedCells,
    deleteSelectedCells,
    copySelectedCellsContent,
    pasteToCells,
    cellIsWithinCopyBoundary,

    clearPopOvers,
    showPopOver,
    showNotInEditModePopOver,
    showNotWritablePopOver,
    handleOpenContextMenu,

    deletePendingProducts,

    // exposed composables
    tableSelection,
  }
})
